<template>
    <div class="flex items-center gap-2">
        <vx-dropdown placement="right" teleport>
            <vx-button data-test="row-actions" type="button" color="muted-light" size="xs" aria-label="Group actions">
                <font-awesome-icon widthAuto :icon="faEllipsis" />
            </vx-button>

            <template v-slot:items>
                <vx-dropdown-item data-test="view-group" :href="props.groupHref">
                    View
                    <font-awesome-icon widthAuto :icon="faEye" />
                </vx-dropdown-item>

                <vx-dropdown-item data-test="change-status" @click="statusModalVisible = true">
                    Change Status
                    <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                </vx-dropdown-item>

                <vx-dropdown-item data-test="delete" @click="deleteGroup">
                    Delete
                    <font-awesome-icon widthAuto :icon="faTrash" />
                </vx-dropdown-item>
            </template>
        </vx-dropdown>

        <vx-popover hover teleport :arrow="true" placement="top">
            <vx-button
                data-test="impersonate"
                :href="impersonateHref"
                :disabled="impersonateDisabled"
                color="muted-light"
                size="xs"
                aria-label="Impersonate"
            >
                <font-awesome-icon widthAuto :icon="faMasksTheater" />
            </vx-button>
            <template v-slot:content>Impersonate</template>
        </vx-popover>
    </div>

    <update-group-status-modal
        v-model:visible="statusModalVisible"
        :teamId="props.teamId"
        :groupId="props.group.group_id"
        :groupName="props.group.name"
        :status="props.group.status"
        :isBillable="props.group.is_billable"
        @updated="emit('statusUpdated', $event)"
    />
</template>

<script lang="ts" setup>
import { faArrowsRotate, faEllipsis, faEye, faMasksTheater, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxDropdown, VxDropdownItem, VxPopover, VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import UpdateGroupStatusModal from '~/components/kiosk/kiosk-groups/list/UpdateGroupStatusModal.vue';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';
import { groupsClient } from '~/services';
import type { GroupStatus } from '~/constants/groupStatuses';
import type { KioskTeamGroup } from '~/services/adminTeamGroupsClient.types';
import { stringifyQueryString } from '~/utils/string';
import { throwSwalRequestError } from '~/utils/request';

const props = defineProps<{
    teamId: number;
    group: KioskTeamGroup;
    groupHref: string;
}>();

const emit = defineEmits<{
    statusUpdated: [status: GroupStatus];
    deleted: [];
}>();

const currentUserId = Spark.state.user.id;

const { team } = useTeamDetails();

const statusModalVisible = ref(false);

const impersonateHref = computed(
    () =>
        `/kiosk/users/${team.value.owner_id}/impersonate?${stringifyQueryString({
            team_id: props.teamId,
            group_id: props.group.group_id,
        })}`
);

const impersonateDisabled = computed(
    () => team.value.owner_id === undefined || team.value.owner_id === currentUserId || team.value.status === 'closed'
);

const deleteGroup = () =>
    VxSwal.confirmation('Delete Group', 'Are you sure? There is no undo.', 'danger', () =>
        groupsClient
            .deleteGroup({ teamId: props.teamId }, props.group.group_id)
            .then(() => emit('deleted'))
            .catch(throwSwalRequestError)
    );
</script>
